import { useCallback, useState } from "react";
import type { ChangeEvent, SyntheticEvent } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { styled, useTheme } from "styled-components";

import { countries, regions } from "simplydo/core/countries";
import {
  accreditations as accreditationOptions,
  businessSizeOptions,
  businessStatusOptions,
  businessTypeOptions,
} from "simplydo/core/ideaBusinessProfiles";
import { asMutation, initUseQuery } from "simplydo/core/query";
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Icon,
  Image,
  Label,
  Message,
  Popup,
  Segment,
} from "simplydo/react/ui";
import type {
  DropdownProps,
  InputOnChangeData,
  InputProps,
  TextAreaProps,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { ImageChooser } from "../../Choosers";
import TagModalChooser from "../../Choosers/Tags/TagModalChooser";
import { TooltipButton } from "../../UI";
import type {
  GatherAdditionalDataResult,
  IdeaBusinessProfile,
  IdeaBusinessProfileUpdate,
} from "../types";

const UploadImageSegment = styled(Segment)`
  min-width: 100%;
  max-width: 200px;
  margin-bottom: 16px !important;

  .ui.image {
    &:hover {
      cursor: pointer;
      opacity: 0.8;
    }
  }
`;

const AccreditationsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  .ui.label {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

type EditOverviewProps = {
  businessProfile: IdeaBusinessProfile;
  setUnsaved?: (unsaved: boolean) => void;
};

export const EditOverview = ({
  businessProfile,
  setUnsaved,
}: EditOverviewProps) => {
  const theme = useTheme();
  const [addingCustomAccreditation, setAddingCustomAccreditation] =
    useState(false);
  const [generatingAdditionalData, setGeneratingAdditionalData] =
    useState(false);
  const [customAccreditation, setCustomAccreditation] = useState("");
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const {
    name,
    description,
    address = {},
    websiteUrl,
    websiteUrlSkipped,
    size,
    type,
    status,
    accreditations: profileAccreditations = [],
    foundingYear,
    creditsafeRegNo,
    creditsafeRegNoSkipped,
    vatNo,
    vatNoSkipped,
    ownerTags,
  } = businessProfile || {};
  const { country, city, streetAddress, postcode, region } = address;
  const tagsQuery = useQuery(
    initUseQuery(
      api.tags.getAllForEntity,
      ["organisation", user?.organisation ?? "", false],
      { isEnabled: !!user?.organisation },
    ),
  );

  const addTagMutation = useMutation({
    mutationKey: ["post", "tags", "ideaBusinessProfiles", "tag"],
    mutationFn: asMutation(api.tags.tagSingle)!,
    onSuccess: (tagData) => {
      api.setQueryData(api.users.getProfile, [businessProfile._id], (prev) => ({
        ideaBusinessProfile: {
          ...prev.ideaBusinessProfile,
          tags: [...(prev.ideaBusinessProfile?.tags ?? []), tagData._id],
          ownerTags: [...(prev.ideaBusinessProfile?.ownerTags ?? []), tagData],
        },
      }));
    },
  });

  const removeTagMutation = useMutation({
    mutationKey: ["delete", "tags", "ideaBusinessProfiles", "tag"],
    mutationFn: asMutation(api.tags.untagSingle)!,
    onSuccess: (tagData) => {
      api.setQueryData(api.users.getProfile, [businessProfile._id], (prev) => ({
        ideaBusinessProfile: {
          ...prev.ideaBusinessProfile,
          tags: prev.ideaBusinessProfile?.tags?.filter(
            (t) => t !== tagData._id,
          ),
          ownerTags: prev.ideaBusinessProfile?.ownerTags?.filter(
            (t) => t._id !== tagData._id,
          ),
        },
      }));
    },
  });

  const updateProfile = useCallback(
    (data: IdeaBusinessProfileUpdate) => {
      api.setQueryData(api.users.getProfile, [businessProfile._id], (prev) => ({
        ideaBusinessProfile: { ...prev.ideaBusinessProfile, ...data },
      }));

      setUnsaved?.(true);
    },
    [setUnsaved, businessProfile],
  );

  const currentYear = new Date().getFullYear();
  const getOrganisationStatus = useCallback(
    (foundingYear: number) => {
      if (foundingYear.toString().length === 4) {
        const diff = currentYear - foundingYear;
        if (diff < 1) {
          return "startup";
        } else if (diff < 6) {
          return "established_small";
        } else if (diff < 11) {
          return "established_medium";
        } else {
          return "established_large";
        }
      }
      return status;
    },
    [status, currentYear],
  );

  const generateDescription = useCallback(() => {
    setGeneratingAdditionalData(true);

    api.users.gatherAdditionalData(
      {
        companyName: name ?? "",
        websiteUrl: websiteUrl ?? "",
        businessProfileId: businessProfile._id,
      },
      (response) => {
        const { success, data } = response;
        if (!success) {
          toast.error("Unable to fetch additional data from website");
          setGeneratingAdditionalData(false);
        } else if (data) {
          const {
            foundingYear: incomingFoundingYear,
            size: incomingSize,
            type: incomingType,
            description: incomingDescription,
            image: incomingImage,
          } = data;
          let newStatus;
          if (incomingFoundingYear) {
            newStatus = getOrganisationStatus(incomingFoundingYear);
          }
          // We only update the fields witht the crunchbase data that are not already filled in
          updateProfile({
            foundingYear: foundingYear || incomingFoundingYear,
            status: status || newStatus,
            size: size || (incomingSize as IdeaBusinessProfile["size"]),
            type: type || (incomingType as IdeaBusinessProfile["type"]),
            description: description || incomingDescription,
            imageUrl: businessProfile.imageUrl || incomingImage,
            image: businessProfile.image || incomingImage,
          });
          setGeneratingAdditionalData(false);
        }
      },
      (err) => {
        toast.error(err.message);
        setGeneratingAdditionalData(false);
      },
    );
  }, [
    websiteUrl,
    name,
    updateProfile,
    getOrganisationStatus,
    businessProfile._id,
    businessProfile.imageUrl,
    businessProfile.image,
    foundingYear,
    status,
    size,
    type,
    description,
  ]);
  return (
    <>
      <Form>
        <div
          style={{
            display: "flex",
            gap: 24,
            flexDirection: theme.sizes.isComputer ? "row" : "column",
          }}
        >
          <ImageChooser
            forType="ideaBusinessProfile"
            forId={businessProfile._id}
            trigger={
              <div>
                <UploadImageSegment>
                  {businessProfile?.imageUrl ?
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignSelf: "flex-start",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 4,
                          overflow: "hidden",
                          maxHeight: 150,
                          width: 150,
                        }}
                      >
                        <Image
                          style={{ objectFit: "contain" }}
                          src={businessProfile?.imageUrl}
                          alt={businessProfile?.name}
                        />
                      </div>
                      <Button
                        size="mini"
                        basic
                        fluid
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          util
                            .confirm(
                              "Remove logo",
                              "Are you sure you want to remove the logo?",
                            )
                            .then(() => {
                              updateProfile({
                                imageUrl: undefined,
                                image: undefined,
                              });
                            })
                            .catch(() => {});
                        }}
                      >
                        <Icon name="trash" />
                        Remove
                      </Button>
                    </div>
                  : <>
                      <Message info>
                        <Message.Header>Upload a logo</Message.Header>
                        <p>
                          We recommend a square image with a size of 500x500
                          pixels in either PNG or JPG format.
                        </p>
                      </Message>
                      <TooltipButton
                        fluid
                        icon="upload"
                        wrapperStyle={{ display: "flex" }}
                        content="Choose image"
                      />
                    </>
                  }
                </UploadImageSegment>
              </div>
            }
            onComplete={(responseFilename, _fn, downloadUrl) => {
              updateProfile({ imageUrl: downloadUrl, image: responseFilename });
            }}
          />
          <div style={{ flex: 1 }}>
            <Form.Field
              control={Input}
              required
              label="Organisation name"
              placeholder="Type your organisation name.."
              defaultValue={name}
              onChange={(_e: SyntheticEvent, { value }: InputProps) => {
                updateProfile({ name: String(value ?? "") });
              }}
            />
            <Form.Group widths={"equal"}>
              <Form.Field
                control={Input}
                label="Phone number"
                placeholder="+44 0000 000000"
                defaultValue={businessProfile.phoneNumber}
                required
                onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                  updateProfile({ phoneNumber: value })
                }
              />
              <Form.Field>
                <Form.Field
                  control={Input}
                  label="Your business' website"
                  required
                  placeholder="https://www.example.com"
                  disabled={websiteUrlSkipped}
                  defaultValue={websiteUrl}
                  onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                    updateProfile({ websiteUrl: value })
                  }
                />
                <Checkbox
                  label="My business does not have a website yet"
                  style={{ marginTop: 15 }}
                  onChange={(_e, { checked }) =>
                    updateProfile({ websiteUrlSkipped: checked })
                  }
                  checked={websiteUrlSkipped}
                />
                {(
                  util.validateUrl(websiteUrl ?? "") &&
                  businessProfile?.crunchbase?.websiteUrl !== websiteUrl
                ) ?
                  <Popup
                    content={`${util.appName()} will lookup the website URL provided in Crunchbase to gather additional data about your organisation.`}
                    trigger={
                      <Button
                        content="Gather additional data from website"
                        floated="right"
                        loading={generatingAdditionalData}
                        onClick={generateDescription}
                        size="tiny"
                        style={{ marginTop: 20 }}
                        icon="magic"
                      />
                    }
                  />
                : null}
              </Form.Field>
            </Form.Group>
          </div>
        </div>
        <Form>
          <Form.TextArea
            label="About the organisation"
            defaultValue={description}
            onChange={(_e: SyntheticEvent, { value }: TextAreaProps) =>
              updateProfile({ description: String(value ?? "") })
            }
          />
        </Form>
        <Divider hidden />
        <h4 style={{ marginBottom: 0 }}>Registered address</h4>
        <p
          style={{ color: "grey", marginTop: 5 }}
        >{`This must be your official registered address as recorded with Companies House (or other official register).  If you are a sole trader or another type of organisation, use your trading address or an address where your business can be reached.`}</p>
        <Form.Group widths={"equal"}>
          <Form.Field
            width={theme.sizes.isComputer ? "8" : "16"}
            control={Input}
            required
            label="Street address"
            defaultValue={streetAddress}
            onChange={(_e: SyntheticEvent, { value }: InputProps) =>
              updateProfile({
                address: { ...(address || {}), streetAddress: value },
              })
            }
          />
          <Form.Select
            width={theme.sizes.isComputer ? "8" : "16"}
            search
            clearable
            required
            label="Country"
            defaultValue={country}
            onChange={(_e: SyntheticEvent, { value }: DropdownProps) => {
              updateProfile({
                address: {
                  ...(address || {}),
                  country: value as string,
                  region:
                    value !== "GB" ? "" : businessProfile?.address?.region,
                },
              });
            }}
            placeholder="Select one"
            options={countries.map((c) => ({
              key: c.iso2,
              flag: c.iso2.toLowerCase(),
              value: c.iso2,
              text: c.name,
            }))}
          />
          {(
            country === "GB" ||
            country === "England" ||
            country === "United Kingdom"
          ) ?
            <Form.Select
              search
              width={theme.sizes.isComputer ? "8" : "16"}
              required
              clearable
              label="Region"
              defaultValue={region}
              onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
                updateProfile({
                  address: { ...(address || {}), region: value as string },
                })
              }
              options={regions.map((r) => ({
                key: r.value,
                value: r.value,
                text: r.name,
              }))}
            />
          : null}
        </Form.Group>
        <Form.Group widths={"equal"}>
          <Form.Field
            control={Input}
            required
            label="City"
            defaultValue={city}
            onChange={(_e: SyntheticEvent, { value }: InputProps) =>
              updateProfile({ address: { ...(address || {}), city: value } })
            }
          />
          <Form.Field
            control={Input}
            required
            label="Postcode"
            defaultValue={postcode}
            onChange={(_e: SyntheticEvent, { value }: InputProps) =>
              updateProfile({
                address: { ...(address || {}), postcode: value },
              })
            }
          />
        </Form.Group>

        <Divider section hidden />
        <h4>Additional information</h4>
        <Form.Group widths="equal">
          <Form.Select
            required
            value={size}
            label={"Organisation size"}
            options={businessSizeOptions}
            onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
              updateProfile({
                size: value as "micro" | "small" | "medium" | "large",
              })
            }
          />
          <Form.Select
            required
            value={type}
            label={"Organisation type"}
            options={businessTypeOptions}
            onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
              updateProfile({
                type: value as
                  | "public"
                  | "private"
                  | "notForProfit"
                  | "academic",
              })
            }
          />
        </Form.Group>
      </Form>
      <Form style={{ marginTop: 20 }}>
        <Form.Group widths={"equal"}>
          <Form.Field
            control={Input}
            type="number"
            placeholder="2011"
            required
            error={
              foundingYear ?
                foundingYear < 1000 || foundingYear > currentYear
              : undefined
            }
            label="Organisation founding year"
            defaultValue={foundingYear}
            onChange={(_e: SyntheticEvent, { value }: InputProps) => {
              const year = parseInt(String(value ?? ""), 10);
              const newStatus = getOrganisationStatus(year);
              updateProfile({ foundingYear: year, status: newStatus });
            }}
          />
          <Form.Select
            required
            value={status}
            label={"Organisation status"}
            options={businessStatusOptions}
            onChange={(_e: SyntheticEvent, { value }: DropdownProps) =>
              updateProfile({
                status: value as
                  | "preStartup"
                  | "startup" // < 1 year
                  | "established_small" // 1-5 years
                  | "established_medium" // 6-10 years
                  | "established_large", // >10 years
              })
            }
          />
        </Form.Group>
      </Form>
      <Divider section />
      <Form>
        <Form.Group widths="equal">
          <Form.Field>
            <Form.Field
              control={Input}
              placeholder="01234567"
              required
              label="Organisation registration number"
              defaultValue={creditsafeRegNo}
              onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                updateProfile({ creditsafeRegNo: value })
              }
              disabled={creditsafeRegNoSkipped}
            />
            <Checkbox
              label="My business does not have a registration number"
              checked={creditsafeRegNoSkipped}
              onChange={(_e, { checked }) =>
                updateProfile({ creditsafeRegNoSkipped: checked })
              }
              style={{ marginTop: 10 }}
            />
          </Form.Field>
          <Form.Field>
            <Form.Field
              control={Input}
              required
              placeholder="GB-000000"
              label="VAT registration number"
              defaultValue={vatNo}
              onChange={(_e: SyntheticEvent, { value }: InputProps) =>
                updateProfile({ vatNo: value })
              }
              disabled={vatNoSkipped}
            />
            <Checkbox
              label="My business does not have a VAT number"
              checked={vatNoSkipped}
              onChange={(_e, { checked }) =>
                updateProfile({ vatNoSkipped: checked })
              }
              style={{ marginTop: 10 }}
            />
          </Form.Field>
        </Form.Group>
      </Form>
      <Divider section hidden />
      <h4>Accreditations</h4>
      <AccreditationsContainer>
        {profileAccreditations.map((accreditation) => (
          <Label key={accreditation} size="medium">
            <span>
              {accreditationOptions.find((a) => a.key === accreditation)
                ?.text || accreditation}
            </span>
            <Icon
              name="delete"
              onClick={() =>
                updateProfile({
                  accreditations: profileAccreditations.filter(
                    (a) => a !== accreditation,
                  ),
                })
              }
            />
          </Label>
        ))}
        <Dropdown
          style={{ fontSize: "small", position: "relative", zIndex: 20 }}
          button
          className="icon"
          labeled
          icon="add"
          scrolling
          text="Add accreditation"
        >
          <Dropdown.Menu>
            {accreditationOptions.map((accreditation) => (
              <Dropdown.Item
                key={accreditation.key}
                defaultValue={accreditation.key}
                onClick={() => {
                  if (profileAccreditations.includes(accreditation.key)) {
                    toast.error("This accreditation is already added");
                    return;
                  }
                  if (accreditation.key === "other") {
                    setAddingCustomAccreditation(true);
                  } else {
                    updateProfile({
                      accreditations: [
                        ...profileAccreditations,
                        accreditation.key,
                      ],
                    });
                  }
                }}
              >
                {accreditation.text}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {addingCustomAccreditation && (
          <>
            <Form.Field
              control={Input}
              placeholder="Enter accreditation"
              defaultValue={customAccreditation}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCustomAccreditation(e.target.value)
              }
            />
            <Button
              onClick={() => {
                updateProfile({
                  accreditations: [
                    ...profileAccreditations,
                    customAccreditation,
                  ],
                });
                setCustomAccreditation("");
                setAddingCustomAccreditation(false);
              }}
            >
              Add
            </Button>
          </>
        )}
      </AccreditationsContainer>
      {(tagsQuery.data?.length ?? 0) > 0 && (
        <>
          <Divider section hidden />
          <h4>Tags</h4>
          <TagModalChooser
            trigger={
              <TooltipButton
                compact
                icon="hashtag"
                content={t("tags.edit")}
                size="tiny"
              />
            }
            tags={ownerTags || []}
            availableTags={
              tagsQuery.data?.filter(
                (tag) =>
                  !tag?.disabledEntities?.includes("ideaBusinessProfiles"),
              ) || []
            }
            canEdit={true}
            addTag={(tag) => {
              addTagMutation.mutate([
                "ideaBusinessProfiles",
                tag._id,
                businessProfile._id,
              ]);
            }}
            removeTag={(tag) => {
              removeTagMutation.mutate([
                "ideaBusinessProfiles",
                tag._id,
                businessProfile._id,
              ]);
            }}
          />
        </>
      )}
    </>
  );
};
